import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';

export default function APIKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch existing keys
  const fetchKeys = async () => {
    try {
      const response = await api.get('/api/keys');
      setKeys(response.data);
    } catch (error) {
      console.error('Failed to fetch keys:', error);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  // Create a new key - FIXED: Now sending the type field
  const createKey = async (keyType) => {
    setLoading(true);
    try {
      // THIS IS THE FIX - sending { type: keyType }
      const response = await api.post('/api/keys', {
        type: keyType  // CRITICAL: Must send 'staging' or 'production'
      });
      
      // console.log('Key created:', response.data);
      
      // Refresh the keys list to show the new key
      await fetchKeys();
      
      // Show appropriate message
      if (keyType === 'production') {
        alert('Production key created! It needs admin approval before it can be used.');
      } else {
        alert('Staging key created successfully!');
      }
    } catch (error) {
      console.error('Failed to create key:', error);
      alert('Failed to create the key:  ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Delete a key
  const deleteKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this key?')) return;
    
    try {
      await api.delete(`/api/keys/${keyId}`);
      await fetchKeys(); // Refresh list
      alert('Key deleted successfully');
    } catch (error) {
      alert('Failed to delete the key');
    }
  };

  // Rotate a key
  const rotateKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to rotate this key?')) return;
    
    try {
      await api.put(`/api/keys/${keyId}/rotate`);
      await fetchKeys(); // Refresh list
      alert('Key rotated successfully');
    } catch (error) {
      alert('Failed to rotate the key');
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-8">API Keys</h1>
      
      {/* Key Generation Section */}
      <div className="bg-white p-6 rounded-lg shadow mb-8">
        <h2 className="text-xl font-semibold mb-4">Generate New Key</h2>
        <div className="flex gap-4 mb-4">
          <button
            onClick={() => createKey('staging')}
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Staging Key'}
          </button>
          <button
            onClick={() => createKey('production')}
            disabled={loading}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Production Key'}
          </button>
        </div>
        <p className="text-sm text-gray-600">
          <strong>Staging keys:</strong> Auto-approved with 500 requests/day limit.<br/>
          <strong>Production keys:</strong> Require admin approval and have 10,000 requests/day limit.
        </p>
      </div>

      {/* Keys List Section */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Your API Keys</h2>
        {keys.length === 0 ? (
          <p className="text-gray-600">No API keys yet. Create one above to get started.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Key</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Limit</th>
                  <th className="text-left p-2">Created</th>
                  <th className="text-right p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map(key => (
                  <tr key={key.id} className="border-b">
                    <td className="p-2">
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                        {key.key.substring(0, 20)}...
                        <button
                          onClick={() => navigator.clipboard.writeText(key.key)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                          title="Copy to clipboard"
                        >
                          📋
                        </button>
                      </code>
                    </td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        key.type === 'production' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {key.type}
                      </span>
                    </td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        key.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {key.is_active ? 'Active' : 'Pending Approval'}
                      </span>
                    </td>
                    <td className="p-2">{key.limit}/day</td>
                    <td className="p-2 text-sm">
                      {new Date(key.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-2 text-right space-x-2">
                      <button
                        onClick={() => rotateKey(key.id)}
                        className="text-blue-600 hover:underline text-sm"
                      >
                        Rotate
                      </button>
                      <button
                        onClick={() => deleteKey(key.id)}
                        className="text-red-600 hover:underline text-sm"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}